import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialog as MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../state/app.state';
import { takeUntil } from 'rxjs/operators';
import { appDataDetails, translations } from '../../../../../../shared/core/shared.selectors';
import { projectForRoute, resolveWidgetConfig } from '../../../../../../shared/helpers/project-widget-config';
import { FetchTranslationsRequest } from '../../../../../../shared/core/shared.actions';
import { stripMapFeatureLinks } from '../../../../../../shared/helpers/design-context';
import {
  CheckIfDailyReportCanBeCreatedRequest,
  ClearDailyReportRequest,
  CopyDailyReportRequest,
  CreateDailyReportDraftRequest,
  FetchDailyReportRequest,
} from '../../../../core/projects.actions';
import * as moment from 'moment/moment';
import { FormControl, FormGroup } from '@angular/forms';
import {
  dailyReport,
  draftDailyReport,
  existingReportId,
  projectDetails,
} from '../../../../core/projects.selectors';

@Component({
    selector: 'app-copy-daily-report',
    templateUrl: './copy-daily-report.component.html',
    styleUrls: ['./copy-daily-report.component.scss'],
    standalone: false
})
export class CopyDailyReportComponent implements OnInit, OnDestroy {
  selectedDate: Date | null = null;
  dateSelected = false;
  translations: any = {};
  report: any = {};
  drWidgetConfig: any = [];
  form: FormGroup = new FormGroup({
    widgets: new FormControl({ value: null, disabled: false }),
  });
  existingReportId: string;
  draftReport: any;
  widgetOptions = [];
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
  }

  ngOnInit() {
    this.store.dispatch(FetchTranslationsRequest({
      payload: {
        projectId: this.data?.report?.projectId,
      },
    }));
    this.store.dispatch(ClearDailyReportRequest());
    this.store.dispatch(FetchDailyReportRequest({
      payload: {
        qp: {
          include: [
            'project_id',
            'site_id',
            'site',
            'project',
            'next-actions',
            'activity_types',
            'visual_inspection_specs',
            {
              control_forms: [
                'fields',
                'control_form',
                'pictures',
                'videos',
                {
                  lines: [
                    'lines',
                    'field_values',
                    'pictures',
                    'videos',

                  ],
                },
                {
                  signatures: [
                    'signatures',
                    'pictures',
                  ],
                },
              ],
            },
            {
              bid_items: [
                'pictures',
                'videos',
                {
                  sources: [
                    'source_group_id',
                  ],
                },
                {
                  bid_item: ['fields'],
                },
                'heading',
                {
                  annotations: ['drawing'],
                },
                'bid_item_id',
                'heading_id',
                'station_id',
                'field_values',
                'station',
              ],
            },
            {
              material_deliveries: [
                'pictures',
                'videos',
                {
                  annotations: ['drawing'],
                },
                'field_values',
                'bid_item_id',
                'bid_item',
                'material',
                'latitude',
                'longitude',
                'field_values',
              ],
            },
            {
              activities: [
                'pictures',
                'videos',
                'address',
                {
                  annotation: ['drawing'],
                },
              ],
            },
            {
              internal_tests: [
                'bid_item',
                'internal_test',
                'internal_test_id',
                'station',
                'heading',
                'field_values',
                'pictures',
                'videos',
                {
                  annotations: ['drawing'],
                },
              ],
            },
            {
              issues: [
                'map_feature_link',
                'entity',
                'pictures',
                'videos',
                'attachments',
                'bid_item',
                'annotation',
                {
                  createdBy: ['picture'],
                },
                {
                  closedBy: ['picture'],
                },
              ],
            },
            {
              labor: [
                'sub_contractor',
              ],
            },
            {
              equipment: [
                'sub_contractor',
              ],
            },
            {
              activity_codes: [
                'activity_codes',
                'pictures',
                'videos',
                {
                  bid_item: [
                    'stations',
                    'fields',
                    'materials',
                  ],
                },
                'field_values',
                'station',
                'material',
                {
                  annotations: [
                    'drawing',
                  ],
                },
                {
                  lines: [
                    'lines',
                    'activity_code',
                  ],
                },
              ],
            },
          ],
        },
        reportId: this.data?.report?.id,
      },
    }));

    // Widgets of the project the report is being copied within: the project's
    // own widget_config when it has one, the org's otherwise. Either of the
    // two can land first; a project that is not this report's is ignored.
    combineLatest([this.store.select(appDataDetails), this.store.select(projectDetails)])
      .pipe(takeUntil(this.onDestroy))
      .subscribe(([appData, project]) => {
        const data = resolveWidgetConfig(appData, projectForRoute(project, this.data?.report?.projectId));

        if (data?.length) {
          const tempData = data?.filter(o => o.display);
          tempData.sort((a, b) => a.display_order - b.display_order);
          this.drWidgetConfig = tempData;

          this.createDailyReportWidgetConfig();
        }
      });

    this.store.select(translations)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((t: any) => {
        this.translations = t || {};

        this.createDailyReportWidgetConfig();
      });

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.report = JSON.parse(JSON.stringify(report));
      });

    this.store.select(existingReportId)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(d => {
        if (d) {
          this.existingReportId = d;
        } else {
          this.existingReportId = null;
        }
      });

    this.store.select(draftDailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(d => {
        if (d.id) {
          this.draftReport = JSON.parse(JSON.stringify(d));
        }
      });
  }

  onDateSelected(date: Date) {
    this.selectedDate = date;
    this.checkIfDailyReportExists(this.data.report, this.selectedDate);
  }

  copy() {
    if (this.selectedDate) {
      this.dateSelected = !this.dateSelected;
    }

    if (this.selectedDate && !this.existingReportId) {
      const payload = {
        siteId: this.report?.site_id,
        report_date: moment(this.selectedDate).format('YYYY-MM-DD'),
        work_condition_code: this.report?.work_condition_code,
      };

      this.store.dispatch(CreateDailyReportDraftRequest({ payload }));
    }
  }

  proceed() {
    const formValues = this.form.getRawValue();
    const selectedKeys = formValues.widgets;

    this.draftReport = JSON.parse(JSON.stringify(this.draftReport));

    const tempControlForms = [];

    selectedKeys?.forEach(key => {
      switch (key) {
        case 'deliveries_and_usage':
          this.draftReport['usage'] = [
            ...(this.report['usage']?.map(item => ({ ...item, id: null })) ?? []),
          ];

          this.draftReport['deliveries'] = [
            ...(this.report['deliveries']?.map(item => ({ ...item, id: null })) ?? []),
          ];
          break;

        case 'labor_and_equipment':
          this.draftReport['labor'] = [
            ...(this.report['labor']?.map(item => ({ ...item, id: null })) ?? []),
          ];

          this.draftReport['equipment'] = [
            ...(this.report['equipment']?.map(item => ({ ...item, id: null })) ?? []),
          ];
          break;

        case 'forms':
        case 'control_forms':
        case 'testing_forms':
          this.report['control_forms']?.forEach(form => {
            if (form.control_form?.display_widget === key) {
              tempControlForms.push({ ...form, id: null });
            }
          });
          break;

        default:
          if (this.report[key]) {
            this.draftReport[key] = this.report[key].map(item => ({ ...item, id: null }));
          }
      }
    });

    if (tempControlForms.length) {
      this.draftReport['control_forms'] = tempControlForms;
    }

    // A copied line is a new capture: it never carries the source line's design context.
    this.draftReport = stripMapFeatureLinks(this.draftReport);

    this.draftReport = this.manageMedia(this.draftReport);

    if (this.draftReport.activity_codes?.length) {
      this.draftReport.activity_codes = this.refactorActivityCodesLines(this.draftReport?.activity_codes);
    }

    if (this.draftReport?.control_forms?.length) {
      this.draftReport.control_forms = this.refactorControlFroms(this.draftReport?.control_forms);
    }

    if (this.draftReport?.labor?.length) {
      this.draftReport.labor = this.refactorLaborList(this.draftReport?.labor);
    }

    if (this.draftReport?.equipment?.length) {
      this.draftReport.equipment = this.refactorEquipmentList(this.draftReport?.equipment);
    }

    this.store.dispatch(
      CopyDailyReportRequest({
        payload: {
          params: { siteId: this.draftReport?.site?.id },
          dailyReport: this.draftReport,
        },
      }),
    );
  }

  createDailyReportWidgetConfig() {
    this.widgetOptions = [];

    if (this.drWidgetConfig?.length > 0) {
      this.widgetOptions = this.drWidgetConfig
        .filter((item: any) => item?.display)
        .sort((a: any, b: any) => a.display_order - b.display_order)
        .map((item: any) => this.getMenuItemsForWidget(item?.name, this.translations))
        .reduce((acc: any, val: any) => acc.concat(val), []);
    }
  }

  getMenuItemsForWidget(name: string, translation: any) {
    switch (name) {
      case 'bid_items':
        return [
          {
            label: 'Bid item',
            value: 'bid_items',
            isChecked: false,
          },
        ];

      case 'deliveries':
        return [
          {
            label: 'Delivery & usage',
            value: 'deliveries_and_usage',
            isChecked: false,
          },
        ];

      case 'labor':
        return [
          {
            label: 'Labor & equipment',
            value: 'labor_and_equipment',
            isChecked: false,
          },
        ];

      case 'activities':
        return [
          {
            label: translation?.ReportActivity?.title || 'Activities',
            value: 'activities',
            isChecked: false,
          },
        ];

      case 'activity_codes':
        return [
          {
            label: 'Activity IDs',
            value: 'activity_codes',
            isChecked: false,
          },
        ];

      case 'forms':
        return [
          {
            label: 'Forms',
            value: 'forms',
            isChecked: false,
          },
        ];

      case 'control_forms':
        return [
          {
            label: 'Control point forms',
            value: 'control_forms',
            isChecked: false,
          },
        ];

      case 'testing_forms':
        return [
          {
            label: 'Testing forms',
            value: 'testing_forms',
            isChecked: false,
          },
        ];

      default:
        return [];
    }
  }

  checkIfDailyReportExists(data: any, date: any) {
    const params = {
      project: data.projectId,
      site: data.site_id,
      date,
    };

    if (params.project && params.site && params.date) {
      this.store.dispatch(CheckIfDailyReportCanBeCreatedRequest({
        payload: {
          project: { id: params.project },
          site: params.site,
          qp: {
            date: moment(params.date).format('YYYY-MM-DD'),
            include: [
              'createdBy',
              'site',
            ],
          },
        },
      }));
    }

  }

  manageMedia(report: any) {
    const resetMedia = (item: any) => ({
      ...item,
      pictures: [],
      videos: [],
    });

    return {
      ...report,

      bid_items: report?.bid_items?.map(resetMedia),
      activities: report?.activities?.map(resetMedia),
      material_deliveries: report?.material_deliveries?.map(resetMedia),
      internal_tests: report?.internal_tests?.map(resetMedia),
      activity_codes: report?.activity_codes?.map(resetMedia),

      control_forms: report?.control_forms?.map(controlForm => ({
        ...resetMedia(controlForm),
        lines: controlForm?.lines?.map(resetMedia) ?? controlForm?.lines,
      })),
    };
  }

  refactorActivityCodesLines(activityCodes) {
    return activityCodes?.map(activityCode => ({
      ...activityCode,
      lines: activityCode?.lines?.map(lineItem => {
        return {
          ...lineItem,
          id: null,
          ...(lineItem.activity_code_id === undefined
              ? { activity_code_id: lineItem?.activity_code?.id }
              : {}
          ),
        };
      }) || [],
    }));
  }

  refactorControlFroms(controlFroms) {
    if (controlFroms?.length > 0) {
      controlFroms.forEach(cf => {
        if (cf?.control_form) {
          cf.control_form_id = cf?.control_form?.id;
        }

        if (cf?.lines.length > 0) {
          cf?.lines.forEach((lineItem) => {
            lineItem.id = null;
          });
        }

        if (cf?.signatures?.length > 0) {
          cf?.signatures.forEach((signature: any) => {
            signature.id = null;
          });
        }
      });
    }

    return controlFroms;
  }

  refactorEquipmentList(equipments) {
    if (equipments?.length > 0) {
      equipments?.forEach(equipment => {
        equipment.sub_contractor_id = equipment?.sub_contractor?.id;
      });
    }

    return equipments;
  }

  refactorLaborList(labors) {
    if (labors?.length > 0) {
      labors?.forEach(labor => {
        labor.sub_contractor_id = labor?.sub_contractor?.id;
      });
    }

    return labors;
  }

  close() {
    this.dialog.closeAll();
  }

  onStatusCheckboxChange(event) {
    this.form.patchValue({
      widgets: event,
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
